<template>
  <BittsModal
    v-model:visible="isVisible"
    title="Configure Crossbeam roles in your IDP"
    :width="680"
    variant="data"
    :show-secondary-button="false"
    primary-button-text="Done"
    @action="emit('closed')"
    @closed="emit('closed')"
  >
    <div class="scim-roles-modal__content">
      <div class="scim-roles-modal__callout">
        <p>
          To automatically assign roles when users are provisioned through your
          Identity Provider (IDP), you need to add two custom attributes to your
          IDP's user profile schema. Once added, set the appropriate value for
          each user you want to sync — Crossbeam will pick up these values
          during provisioning.
        </p>
      </div>

      <div class="scim-roles-modal__section">
        <div class="scim-roles-modal__section-header">
          <div class="font-bold text-neutral-text-strong">Core Role</div>
          <div class="scim-roles-modal__attribute-meta">
            <div class="scim-roles-modal__attribute-row">
              <span class="text-neutral-text-weak text-sm">
                Attribute name
              </span>
              <ScimCopyBadge value="core_role" />
            </div>
            <div class="scim-roles-modal__attribute-row">
              <span class="text-neutral-text-weak text-sm">
                SCIM extension
              </span>
              <ScimCopyBadge :value="SCIM_EXTENSION" />
            </div>
          </div>
        </div>
        <div class="scim-roles-modal__roles-list">
          <div class="scim-roles-modal__column-headers">
            <span>Role name</span>
            <span>Value</span>
          </div>
          <div
            v-for="role in scimCoreRoles"
            :key="role.id"
            class="scim-roles-modal__role-row"
          >
            <BittsInput
              :model-value="role.name"
              :read-only="true"
              :allow-copy="true"
              :name="`core-role-name-${role.id}`"
              class="flex-1"
            />
            <BittsInput
              :model-value="String(role.id)"
              :read-only="true"
              :allow-copy="true"
              :name="`core-role-value-${role.id}`"
              class="flex-1"
            />
          </div>
        </div>
      </div>

      <BittsDivider />

      <div class="scim-roles-modal__section">
        <div class="scim-roles-modal__section-header">
          <div class="font-bold text-neutral-text-strong">Sales Role</div>
          <div class="scim-roles-modal__attribute-meta">
            <div class="scim-roles-modal__attribute-row">
              <span class="text-neutral-text-weak text-sm">
                Attribute name
              </span>
              <ScimCopyBadge value="sales_role" />
            </div>
            <div class="scim-roles-modal__attribute-row">
              <span class="text-neutral-text-weak text-sm">
                SCIM extension
              </span>
              <ScimCopyBadge :value="SCIM_EXTENSION" />
            </div>
          </div>
        </div>
        <div class="scim-roles-modal__roles-list">
          <div class="scim-roles-modal__column-headers">
            <span>Role name</span>
            <span>Value</span>
          </div>
          <div
            v-for="role in SALES_EDGE_ROLES_FOR_SCIM"
            :key="role.id"
            class="scim-roles-modal__role-row"
          >
            <BittsInput
              :model-value="role.name"
              :read-only="true"
              :allow-copy="true"
              :name="`sales-role-name-${role.id}`"
              class="flex-1"
            />
            <BittsInput
              :model-value="String(role.id)"
              :read-only="true"
              :allow-copy="true"
              :name="`sales-role-value-${role.id}`"
              class="flex-1"
            />
          </div>
        </div>
      </div>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import { BittsDivider, BittsInput, BittsModal } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import { NO_ACCESS, SALES, SALES_EDGE_ROLES } from '@/constants/team';
import { useRolesStore } from '@/stores';

import ScimCopyBadge from './ScimCopyBadge.vue';

const emit = defineEmits<{
  closed: [];
}>();

const SCIM_EXTENSION =
  'urn:ietf:params:scim:schemas:extension:crossbeam:2.0:User';

const rolesStore = useRolesStore();
const { allRoles } = storeToRefs(rolesStore);

/** Same rule as SelectCoreRole: sales-seat roles are not valid `core_role` values. */
const scimCoreRoles = computed(() =>
  allRoles.value.filter((role) => role.seat_type !== SALES),
);

const SALES_EDGE_ROLES_FOR_SCIM = SALES_EDGE_ROLES.filter(
  (role) => role.id !== NO_ACCESS,
);

const isVisible = ref(true);

watch(isVisible, (newVal) => {
  if (!newVal) {
    emit('closed');
  }
});
</script>

<style scoped lang="pcss">
@reference "../../../../css/theme.css";

.scim-roles-modal__content {
  @apply flex flex-col gap-24 py-24;
}

.scim-roles-modal__callout {
  @apply bg-neutral-background-weak rounded-lg p-16 text-sm text-neutral-text leading-relaxed;
}

.scim-roles-modal__section {
  @apply flex flex-col gap-12;
}

.scim-roles-modal__section-header {
  @apply flex flex-col gap-8;
}

.scim-roles-modal__attribute-meta {
  @apply flex flex-col gap-6;
}

.scim-roles-modal__attribute-row {
  @apply flex items-center gap-8;
}

.scim-roles-modal__column-headers {
  @apply flex gap-12 text-xs font-medium text-neutral-text-weak px-4;

  & > span {
    @apply flex-1;
  }
}

.scim-roles-modal__roles-list {
  @apply flex flex-col gap-8;
}

.scim-roles-modal__role-row {
  @apply flex items-center gap-12;
}
</style>
